import { memo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import GoogleOAuthButton from './GoogleOAuthButton'
import { getAuthInputClasses, errorMessageClasses } from '../../styles/inputStyles'
import { useAppConfig } from '../../context/AppConfigContext'
import { trackEvent } from '../../utils/analytics'

/**
 * UnifiedAuthForm - Consolidated authentication form component
 * Handles both login and registration with mode switching
 * 
 * Replaces:
 * - LoginTab.jsx (~144 lines)
 * - SignupTab.jsx (~176 lines)
 * - LoginForm.jsx (~136 lines)
 * - RegisterForm.jsx (~193 lines)
 * 
 * Features:
 * - Single unified component for login/signup
 * - Mode-aware field rendering
 * - Google OAuth integration
 * - Form validation error display
 * - Remember me checkbox (login mode)
 * - Forgot password link (login mode)
 * - Mode switching (for embedded use)
 * 
 * @param {string} mode - 'login' or 'register'
 * @param {object} formData - Form field values
 * @param {object} errors - Validation errors
 * @param {function} onChange - Field change handler
 * @param {function} onSubmit - Form submit handler
 * @param {boolean} isLoading - Submit loading state
 * @param {function} onGoogleAuth - Google OAuth handler
 * @param {function} onSwitchMode - Mode switch handler (optional, for embedded use)
 * @param {boolean} showHeader - Show title and mode switch text (default: true)
 * @param {boolean} immersiveMode - Use immersive styling (backdrop blur, etc)
 * 
 * @idempotent - Pure form component with external state
 * @atomic - Single responsibility: unified auth form (~180 lines)
 */
const UnifiedAuthForm = memo(function UnifiedAuthForm({
  mode = 'login',
  formData,
  errors = {},
  onChange,
  onSubmit,
  isLoading = false,
  onGoogleAuth,
  onSwitchMode,
  onFieldFocus,
  onFieldBlur,
  showHeader = true,
  immersiveMode = false
}) {
  const { t } = useTranslation()
  const { getVariant } = useAppConfig()
  const isLogin = mode === 'login'

  // A/B test: signup CTA copy — 'control' vs 'value_cta'
  const signupCtaVariant = !isLogin ? getVariant('signup_cta', ['control', 'value_cta']) : null

  // Track CTA variant impression once per form show
  useEffect(() => {
    if (!isLogin && signupCtaVariant) {
      trackEvent('Experiment', 'impression', 'signup_cta', null, {
        experiment_id: 'signup_cta',
        variant: signupCtaVariant
      })
    }
  }, [isLogin, signupCtaVariant])
  const borderClass = immersiveMode ? 'border-gray-200/50' : 'border-gray-300'
  const inputBgClass = immersiveMode ? 'bg-white/90 backdrop-blur-sm shadow-sm' : 'bg-white'
  const dividerBorderClass = immersiveMode ? 'border-gray-200/50' : 'border-gray-300'

  const handleFocus = (e) => {
    if (onFieldFocus) {
      onFieldFocus(e.target.name)
    }
  }

  const handleBlur = (e) => {
    if (onFieldBlur) {
      onFieldBlur(e.target.name, e.target.value)
    }
  }

  return (
    <>
      {showHeader && (
        <div>
          <h2 className="text-center text-2xl font-bold text-gray-900">
            {isLogin ? t('auth.login.title') : t('auth.register.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {isLogin ? (
              <>
                {t('auth.login.or')}{' '}
                {onSwitchMode ? (
                  <button 
                    onClick={() => onSwitchMode('register')}
                    className="font-medium text-primary hover:text-primary-500"
                  >
                    {t('auth.login.signUpLink')}
                  </button>
                ) : (
                  <Link to="/register" className="font-medium text-primary hover:text-primary-500">
                    {t('auth.login.signUpLink')}
                  </Link>
                )}
              </>
            ) : (
              <>
                {t('auth.register.hasAccount')}{' '}
                {onSwitchMode ? (
                  <button 
                    onClick={() => onSwitchMode('login')}
                    className="font-medium text-primary hover:text-primary-500"
                  >
                    {t('auth.register.signInLink')}
                  </button>
                ) : (
                  <Link to="/login" className="font-medium text-primary hover:text-primary-500">
                    {t('auth.register.signInLink')}
                  </Link>
                )}
              </>
            )}
          </p>
        </div>
      )}
      
      <form className="space-y-3" onSubmit={onSubmit}>
        <div className="space-y-2.5">
          {/* Registration-specific: Name fields */}
          {!isLogin && (
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label htmlFor="firstName" className="block text-xs font-medium text-gray-700 mb-0.5">
                  {t('auth.register.firstName')}
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName || ''}
                  onChange={onChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className={getAuthInputClasses(!!errors.firstName)}
                  placeholder={t('auth.register.firstNamePlaceholder') || 'John'}
                />
                {errors.firstName && (
                  <p className={errorMessageClasses}>{errors.firstName}</p>
                )}
              </div>
              <div>
                <label htmlFor="lastName" className="block text-xs font-medium text-gray-700 mb-0.5">
                  {t('auth.register.lastName')}
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName || ''}
                  onChange={onChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className={getAuthInputClasses(!!errors.lastName)}
                  placeholder={t('auth.register.lastNamePlaceholder') || 'Doe'}
                />
                {errors.lastName && (
                  <p className={errorMessageClasses}>{errors.lastName}</p>
                )}
              </div>
            </div>
          )}

          {/* Email field */}
          <div>
            <label htmlFor={isLogin ? "email" : "email-register"} className="block text-xs font-medium text-gray-700 mb-0.5">
              {isLogin ? t('auth.login.email') : t('auth.register.email')}
            </label>
            <input
              id={isLogin ? "email" : "email-register"}
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email || ''}
              onChange={onChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={getAuthInputClasses(!!errors.email)}
              placeholder={isLogin ? (t('auth.login.emailPlaceholder') || 'you@example.com') : (t('auth.register.emailPlaceholder') || 'you@example.com')}
            />
            {errors.email && (
              <p className={errorMessageClasses}>{errors.email}</p>
            )}
          </div>

          {/* Password field */}
          <div>
            <label htmlFor={isLogin ? "password" : "password-register"} className="block text-xs font-medium text-gray-700 mb-0.5">
              {isLogin ? t('auth.login.password') : t('auth.register.password')}
            </label>
            <input
              id={isLogin ? "password" : "password-register"}
              name="password"
              type="password"
              autoComplete={isLogin ? "current-password" : "new-password"}
              required
              value={formData.password || ''}
              onChange={onChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={getAuthInputClasses(!!errors.password)}
              placeholder={isLogin ? (t('auth.login.passwordPlaceholder') || '••••••••') : (t('auth.register.passwordPlaceholder') || '••••••••')}
            />
            {errors.password && (
              <p className={errorMessageClasses}>{errors.password}</p>
            )}
            {!isLogin && t('auth.register.passwordHint', 'Your password must be at least 8 characters long.') && (
              <p className="mt-0.5 text-xs text-gray-500">{t('auth.register.passwordHint', 'Your password must be at least 8 characters long.')}</p>
            )}
          </div>

          {/* Registration-specific: Confirm password */}
          {!isLogin && (
            <div>
              <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-700 mb-0.5">
                {t('auth.register.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={formData.confirmPassword || ''}
                onChange={onChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                className={getAuthInputClasses(!!errors.confirmPassword)}
                placeholder={t('auth.register.confirmPasswordPlaceholder') || '••••••••'}
              />
              {errors.confirmPassword && (
                <p className={errorMessageClasses}>{errors.confirmPassword}</p>
              )}
            </div>
          )}
        </div>

        {/* Login-specific: Remember me and forgot password */}
        {isLogin && (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-gray-700">
                {t('auth.login.rememberMe')}
              </label>
            </div>

            <div>
              <Link to="/forgot-password" className="font-medium text-primary hover:text-primary-500">
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
          </div>
        )}

        {/* Submit button */}
        <div className="pt-1">
          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading 
              ? (isLogin ? t('auth.login.signingIn') : t('auth.register.creatingAccount'))
              : (isLogin 
                  ? t('auth.login.signInButton') 
                  : (signupCtaVariant === 'value_cta' 
                      ? t('auth.register.createAccountValueCta') 
                      : t('auth.register.createAccount'))
                )
            }
          </button>
        </div>

        {/* Google OAuth */}
        <div className="relative pt-1">
          <div className="absolute inset-0 flex items-center">
            <div className={`w-full border-t ${dividerBorderClass}`} />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">
              {t('auth.login.orContinueWith') || 'Or continue with'}
            </span>
          </div>
        </div>

        <GoogleOAuthButton
          onClick={onGoogleAuth}
          disabled={isLoading}
          text={isLogin ? t('auth.login.googleSignIn') : t('auth.register.signUpWithGoogle')}
        />
      </form>
    </>
  )
})

export default UnifiedAuthForm
